import {Control} from 'ol/control';
import GIS from "../app"
import {MeasurementOptions} from "../configuration/control";
import {Emitter} from "../base/emitter";

class MeasurementControl extends Control {
    private readonly GIS;
    private emitter: Emitter;

    private readonly removeMeasurement: HTMLButtonElement;
    private readonly areaMeasurement: HTMLButtonElement;
    private readonly lineMeasurement: HTMLButtonElement;
    private readonly circleMeasurement: HTMLButtonElement;
    private readonly measurementButton: HTMLButtonElement;
    private readonly measurementButtonGroup: HTMLDivElement;

    private visibleButtons = false;
    constructor(GIS: GIS, options: MeasurementOptions, emitter: Emitter) {
        const element = document.createElement('div');
        element.className = 'cfm-tool cfm-measurement ol-unselectable ol-control';
        super({ element, target: options.target });

        this.emitter = emitter;

        this.GIS = GIS;
        this.removeMeasurement = document.createElement('button');
        this.areaMeasurement = document.createElement('button');
        this.lineMeasurement = document.createElement('button');
        this.circleMeasurement = document.createElement('button');
        this.measurementButton = document.createElement('button');
        this.measurementButtonGroup = document.createElement('div');

        let area = true;
        let line = true;
        let circle = true;

        if (options) {
            area = !!options.area;
            line = !!options.line;
            circle = !!options.circle;
        }

        const hideButtons = () => {
            this.removeMeasurement.style.display = '';
            if (area)   this.areaMeasurement.style.display   = 'none';
            if (line)   this.lineMeasurement.style.display   = 'none';
            if (circle) this.circleMeasurement.style.display = 'none';
        };

        const showButtons = () => {
            this.removeMeasurement.style.display = 'none';
            if (area)   this.areaMeasurement.style.display   = '';
            if (line)   this.lineMeasurement.style.display   = '';
            if (circle) this.circleMeasurement.style.display = '';
        };

        const deactivateInteraction = () => {

            if (options.deactivate) {
                options.deactivate.forEach(name => this.GIS.Interaction.setActive(name, false));
            }
        };

        const activateInteraction = () => {
            if (options.deactivate) {
                options.deactivate.forEach(name => this.GIS.Interaction.setActive(name, true));
            }
        };

        const removeEdit = () => {
            this.GIS.Edit.commit();
            if (area)   this.GIS.Layer.removeFeatures(options.area!);
            if (line)   this.GIS.Layer.removeFeatures(options.line!);
            if (circle) this.GIS.Layer.removeFeatures(options.circle!);
            this.removeMeasurement.style.display = 'none';
            this.visibleButtons = false;

            activateInteraction();
        };

        const chooseMeasurement = () => {
            this.visibleButtons = !this.visibleButtons;
            this.visibleButtons ? showButtons() : hideButtons();
            if (!this.visibleButtons) removeEdit();
            this.emitter.emit('legendHide', this.visibleButtons);
            this.emitter.emit('layersHide', this.visibleButtons);
        };

        const measureArea = () => {
            deactivateInteraction();
            hideButtons();
            this.GIS.Edit.drawFeatures(options.area!, 'Polygon', false);
        };

        const measureLine = () => {
            deactivateInteraction();
            hideButtons();
            this.GIS.Edit.drawFeatures(options.line!, 'LineString', false);
        };

        const measureCircle = () => {
            deactivateInteraction();
            hideButtons();
            this.GIS.Edit.drawFeatures(options.circle!, 'Circle', false);
        };

        if (line || area || circle) {
            this.createMeasurementButton(options);
            element.appendChild(this.measurementButton);

            this.measurementButton.addEventListener('click', chooseMeasurement, false);

            this.createButtonGroup();
            element.appendChild(this.measurementButtonGroup);

            this.createRemoveButton(options);
            this.measurementButtonGroup.appendChild(this.removeMeasurement);

            this.removeMeasurement.addEventListener('click', removeEdit, false);
        }

        if (area) {
            this.createAreaButton(options);
            this.measurementButtonGroup.appendChild(this.areaMeasurement);

            this.areaMeasurement.addEventListener('click', measureArea, false);
        }

        if (line) {
            this.createLineButton(options);
            this.measurementButtonGroup.appendChild(this.lineMeasurement);

            this.lineMeasurement.addEventListener('click', measureLine, false);
        }

        if (circle) {
            this.createCircleButton(options);
            this.measurementButtonGroup.appendChild(this.circleMeasurement);
            this.circleMeasurement.addEventListener('click', measureCircle, false);
        }
    }

    private createLineButton (options: MeasurementOptions) {
        this.lineMeasurement.innerHTML = '<i class="fal fa-wave-triangle"></i>';
        this.lineMeasurement.id = 'cfm-line-button';
        this.GIS.Localisation.bindTitle(
            this.lineMeasurement,
            options.i18n?.lineTip,
            options.lineTip ?? 'Line Measurement'
        );
        this.lineMeasurement.type = 'button';
        this.lineMeasurement.style.display = 'none';
    }

    private createAreaButton (options: MeasurementOptions) {
        this.areaMeasurement.innerHTML = '<i class="fal fa-draw-polygon"></i>';
        this.areaMeasurement.id = 'cfm-area-button';
        this.GIS.Localisation.bindTitle(
            this.areaMeasurement,
            options.i18n?.areaTip,
            options.areaTip ?? 'Area Measurement'
        );
        this.areaMeasurement.type = 'button';
        this.areaMeasurement.style.display = 'none';
    }

    private createCircleButton (options: MeasurementOptions) {
        this.circleMeasurement.innerHTML = '<i class="fal fa-bullseye"></i>';
        this.circleMeasurement.id = 'cfm-circle-button';
        this.GIS.Localisation.bindTitle(
            this.circleMeasurement,
            options.i18n?.circleTip,
            options.circleTip ?? 'Circle Measurement'
        );
        this.circleMeasurement.type = 'button';
        this.circleMeasurement.style.display = 'none';
    }

    private createRemoveButton (options: MeasurementOptions) {
        this.removeMeasurement.innerHTML = '<i class="fal fa-trash-alt"></i>';
        this.removeMeasurement.id = 'cfm-remove-button';
        this.GIS.Localisation.bindTitle(
            this.removeMeasurement,
            options.i18n?.removeTip,
            options.removeTip ?? 'Remove measurement'
        );
        this.removeMeasurement.type = 'button';
        this.removeMeasurement.style.display = 'none';
    }

    private createMeasurementButton (options: MeasurementOptions) {
        this.measurementButton.innerHTML = '<i class="fal fa-ruler-combined"></i>';
        this.measurementButton.id = 'cfm-measurement-button';
        this.GIS.Localisation.bindTitle(
            this.measurementButton,
            options.i18n?.tip,
            options.tip ?? 'Choose measurement type'
        );
        this.measurementButton.type = 'button';
    }

    private createButtonGroup = () => {
        this.measurementButtonGroup.className = 'measurement-action-btn-group';
    }
}

export default MeasurementControl;